/**
 * HeroImg — LCP-optimised <picture> wrapper.
 *
 * Emits AVIF → WebP → original (PNG/JPG) fallback in that order, with
 * responsive `srcset` so mobile pulls the 750px variant and desktop pulls
 * the 1600px one. Fires LCP in <1s on 4G for canonical guide/calculator
 * pages after the pass-91 image pipeline.
 *
 * Usage:
 *   <HeroImg
 *     src="/evergreen-heroes/attic-insulation-cost-guide-2026.jpg"
 *     alt="Insulation crew installing blown-in cellulose in an attic"
 *     priority       // <— only for the SINGLE hero above-the-fold
 *     width={1600}
 *     height={900}
 *   />
 *
 * AVIF/WebP <source>s are emitted only for folders processed by
 * `scripts/optimize-heroes.mjs` (keep OPTIMIZED_FOLDERS in sync with its
 * TARGET_FOLDERS). Other folders render a plain <img>: a missing variant is
 * served as the SPA HTML shell (200 text/html), which <picture> does NOT
 * fall back from — the image would render broken.
 */
import React from "react";

const OPTIMIZED_FOLDERS = [
  "calc-heroes",
  "evergreen-heroes",
  "basement-heroes",
  "bathroom-heroes",
  "deck-heroes",
  "disaster-heroes",
  "fence-heroes",
  "flooring-heroes",
  "hardscape-heroes",
  "kitchen-heroes",
  "landscape-heroes",
  "painting-heroes",
  "pool-heroes",
  "roof-heroes",
  "solar-heroes",
  "window-heroes",
].map((f) => `/${f}/`);

function withSuffix(src, insert) {
  // "/evergreen-heroes/foo.png" + "-mobile" → "/evergreen-heroes/foo-mobile.png"
  const dot = src.lastIndexOf(".");
  if (dot === -1) return src;
  return src.slice(0, dot) + insert + src.slice(dot);
}
function withExt(src, newExt) {
  const dot = src.lastIndexOf(".");
  if (dot === -1) return src + newExt;
  return src.slice(0, dot) + newExt;
}

export default function HeroImg({
  src,
  alt,
  priority = false,
  width = 1600,
  height = 900,
  className,
  sizes = "(max-width: 768px) 100vw, 1600px",
  ...rest
}) {
  const img = (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      decoding={priority ? "sync" : "async"}
      className={className}
      {...rest}
    />
  );
  if (!OPTIMIZED_FOLDERS.some((f) => src?.startsWith(f))) return img;

  const avifDesktop = withExt(src, ".avif");
  const webpDesktop = withExt(src, ".webp");
  const avifMobile = withExt(withSuffix(src, "-mobile"), ".avif");
  const webpMobile = withExt(withSuffix(src, "-mobile"), ".webp");

  return (
    <picture>
      <source
        type="image/avif"
        srcSet={`${avifMobile} 750w, ${avifDesktop} 1600w`}
        sizes={sizes}
      />
      <source
        type="image/webp"
        srcSet={`${webpMobile} 750w, ${webpDesktop} 1600w`}
        sizes={sizes}
      />
      {img}
    </picture>
  );
}
